import { useEffect, useState } from 'react';
import { Database, CheckCircle, XCircle, Loader } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface TableStatus {
  name: string;
  exists: boolean;
  count: number;
}

export function DatabaseStatus() {
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [tables, setTables] = useState<TableStatus[]>([]);

  useEffect(() => {
    checkDatabaseStatus();
  }, []);

  async function checkDatabaseStatus() {
    setLoading(true);
    
    try {
      // Test connection
      const { error: connectionError } = await supabase
        .from('profiles')
        .select('count')
        .limit(1);

      if (connectionError) {
        setConnected(false);
        setLoading(false);
        return;
      }

      setConnected(true);

      // Check key tables
      const tablesToCheck = [
        'customers',
        'drivers',
        'trucks',
        'trailers',
        'loads',
        'invoices',
      ];

      const tableStatuses: TableStatus[] = [];

      for (const tableName of tablesToCheck) {
        const { count, error } = await supabase
          .from(tableName)
          .select('*', { count: 'exact', head: true });

        tableStatuses.push({
          name: tableName,
          exists: !error,
          count: count || 0,
        });
      }

      setTables(tableStatuses);
    } catch (error) {
      console.error('Database check failed:', error);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3">
          <Loader className="w-5 h-5 animate-spin text-blue-600" />
          <span className="text-slate-700">Checking database connection...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-3 mb-4">
        <Database className="w-6 h-6 text-blue-600" />
        <h2 className="text-xl font-bold text-slate-900">Database Status</h2>
      </div>

      <div className="space-y-4">
        {/* Connection Status */}
        <div className="flex items-center gap-2">
          {connected ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-700 font-medium">Connected to Supabase</span>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-700 font-medium">Connection Failed</span>
            </>
          )}
        </div>

        {/* Tables Status */}
        {connected && (
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Tables</h3>
            <div className="grid grid-cols-2 gap-3">
              {tables.map((table) => (
                <div
                  key={table.name}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    {table.exists ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                    <span className="text-sm font-medium text-slate-700 capitalize">
                      {table.name}
                    </span>
                  </div>
                  <span className="text-xs text-slate-500">
                    {table.count} records
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Success Message */}
        {connected && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-green-800">
              🎉 <strong>Database is fully operational!</strong> All tables are created and ready to use.
            </p>
            <p className="text-xs text-green-700 mt-2">
              You can now start creating loads, managing drivers, and tracking your fleet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
